import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { OrderLineModel, OrderModel, ShipmentPackageDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SmallHeadingAndTextOrderLineDelivery from "@WhiteCap/Components/SmallHeadingAndTextOrderLineDelivery";
import StatusLableAndHeading from "@WhiteCap/Components/StatusLableAndHeading";
import { OrderLinesListStyles } from "@WhiteCap/Overrides/Components/OrderLinesList";
import { GroupedOrderLines, ShipmentPackageExtended } from "@WhiteCap/Overrides/Widgets/OrderDetails/OrderDetailsSummaryTable";
import CustomApplicationState from "@WhiteCap/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum fields {
    groupByShipmentPackage = "groupByShipmentPackage",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.groupByShipmentPackage]: boolean;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{
        orderId?: string;
    }>(location.search);
    const orderNumber = parsedQuery.orderId ?? parsedQuery.orderId;
    return {
        orderData: orderNumber ? state.WhiteCap.customOrderStatusPage.orderStatusById[orderNumber] : null,
        pageType: getCurrentPage(state).type,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface OrderDetailsInvoiceTableStyles {
    orderLines?: OrderLinesListStyles;
    gridContainer?: GridContainerProps;
    carrierHeadingAndText?: SmallHeadingAndTextStyles;
    firstCell?: InjectableCss;
    SecondCell?: InjectableCss;
    invoiceNumberHeadingText?: SmallHeadingAndTextStyles;
    statusHeadingText?: SmallHeadingAndTextStyles;
    invoiceHistoryWrapper?: InjectableCss;
    invoiceItemWrapper?: InjectableCss;
}

export const invoiceTableStyles: OrderDetailsInvoiceTableStyles = {
    firstCell: {
        css: css`
            width: auto;
            height: 100%;
            padding: 0 clamp(6px, 1vw, 12px); /* min 6px, max 12px */
            display: flex;
            align-items: center;
            p {
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        min-height: 55px;
                    `,
                    css`
                        min-height: 55px;
                    `,
                    css`
                        min-height: 50px;
                        min-width: 124px;
                    `,
                    css`
                        min-height: 50px;
                        min-width: 124px;
                    `,
                    css`
                        min-height: 50px;
                        min-width: 124px;
                    `,
                ])}
        `,
    },
    SecondCell: {
        css: css`
            display: flex;
            padding: 0 clamp(6px, 1vw, 12px); /* min 6px, max 12px */
            align-items: center;
            p {
                text-align: left;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        p {
                            text-align: center;
                        }
                        min-height: 55px;
                    `,
                    css`
                        p {
                            text-align: center;
                        }
                        min-height: 50px;
                        white-space: nowrap;
                    `,
                    css`
                        min-height: 50px;
                        white-space: nowrap;
                    `,
                    css`
                        min-height: 50px;
                        white-space: nowrap;
                    `,
                    css`
                        min-height: 50px;
                        white-space: nowrap;
                    `,
                ])}
        `,
    },
    carrierHeadingAndText: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 500;
                font-size: 12px !important;
                min-width: max-content;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                font-size: 12px !important;
                font-weight: 350;
                min-width: max-content;
            `,
        },
    },
    statusHeadingText: {
        heading: {
            css: css`
                display: none;
            `,
        },
    },
    invoiceNumberHeadingText: {
        heading: {
            css: css`
                color: ${getColor("text.main")};
                font-weight: 350 !important;
                font-size: 12px !important;
            `,
        },
        text: {
            css: css`
                color: ${getColor("text.main")};
                font-size: 12px !important;
                font-weight: 500 !important;
            `,
        },
    },
    invoiceHistoryWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
            & > div {
                flex: 1 1 min(100%, 48%);
            }
        `,
    },
    invoiceItemWrapper: {
        css: css`
            width: 100%;
            display: flex;
            background: ${getColor("common.accent")} !important;
            @media print {
                background: ${getColor("common.accent")} !important;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css`
                        padding: 0px 16px;
                    `,
                    css`
                        padding: 0px 16px;
                    `,
                    css`
                        padding: 0px 16px;
                    `,
                ])}
        `,
    },
};

const styles = invoiceTableStyles;

const OrderDetailsInvoiceTable: React.FunctionComponent<Props> = ({ fields, orderData, pageType }) => {
    const order = pageType === "CustomOrderStatusPage" ? orderData : useContext(OrderStateContext).value;
    if (!order || !order.orderLines) {
        return null;
    }


    const shipmentStatusByshipmentPackageId = Object.entries(
    JSON.parse(order?.properties?.shipmentPackageExtended ?? "{}")
).reduce(
    (acc, [shipmentPackageId, item]: [string, any]) => {
        acc[shipmentPackageId] = {
            invoiceNumber: item.InvoiceNumber,
            shipmentStatus: item.ShipmentStatus,
        };
        return acc;
    },
    {} as Record<string, { invoiceNumber: string; shipmentStatus: string }>
);

    const groupByShipmentPackageId = (orderLines: OrderLineModel[], shipmentPackages: ShipmentPackageDto[]) => {
        return shipmentPackages.reduce<{ [shipmentPackageId: string]: GroupedOrderLines }>(
            (grouped, shipmentPackage) => {
                const shipmentPackageId = shipmentPackage.id;

                if (!grouped[shipmentPackageId]) {
                    grouped[shipmentPackageId] = {
                        orderLine: [],
                    };
                }
                grouped[shipmentPackageId].shipmentPackageId = shipmentPackageId;
                grouped[shipmentPackageId].deliveryId = shipmentPackage?.packageNumber?.split("-")[0];
                grouped[shipmentPackageId].shipmentDate = shipmentPackage?.shipmentDate;
                grouped[shipmentPackageId].carrier =
                    shipmentPackage?.carrier || order?.shipViaDescription || order?.shipCode;

                shipmentPackage.shipmentPackageLineDtos?.forEach(shipmentPackageLine => {
                    const matchedOrderLine = orderLines.find(
                        orderLine => orderLine.id === shipmentPackageLine.orderLineId,
                    );
                    if (matchedOrderLine) {
                        grouped[shipmentPackageId].orderLine.push(matchedOrderLine);
                    }
                });
                return grouped;
            },
            {},
        );
    };

    const groupByDeliveryId = (orderLines: OrderLineModel[], shipmentPackages?: ShipmentPackageDto[] | null) => {
        const shipmentPackagesMap: { [packageNumber: string]: ShipmentPackageDto } = {};

        if (shipmentPackages) {
            shipmentPackages.forEach(shipmentPackage => {
                shipmentPackagesMap[shipmentPackage.packageNumber.split("-")[0]] = shipmentPackage;
            });
        }

        return orderLines.reduce<{ [deliveryId: string]: GroupedOrderLines }>((grouped, orderLine) => {
            let deliveryIds: string[] = [];
            if (orderLine.properties?.deliveryId?.includes(",")) {
                deliveryIds = orderLine.properties.deliveryId.split(",").map(id => id.trim());
            } else {
                deliveryIds.push(orderLine.properties?.deliveryId?.trim());
            }

            deliveryIds.forEach(deliveryId => {
                // Skip null or undefined deliveryIds
                if (!deliveryId) {
                    return;
                }
                if (grouped[deliveryId]) {
                    grouped[deliveryId] = {
                        orderLine: [],
                    };
                }
                grouped[deliveryId].shipmentDate = shipmentPackagesMap[deliveryId]?.shipmentDate;
                grouped[deliveryId].orderLine.push({ ...orderLine });
                grouped[deliveryId].carrier =
                    shipmentPackagesMap[deliveryId]?.carrier || order?.shipViaDescription || order?.shipCode;
                grouped[deliveryId].deliveryId = deliveryId;
                const shipmentPackage = shipmentPackagesMap[deliveryId];
                grouped[deliveryId].shipmentPackageId = shipmentPackage.id;
                if (shipmentPackage?.shipmentPackageLineDtos) {
                    shipmentPackage.shipmentPackageLineDtos.forEach(shipmentPackageLine => {
                        let matchedOrderLine = grouped[deliveryId].orderLine.find(
                            orderLine => orderLine.id === shipmentPackageLine.orderLineId,
                        );
                        if (matchedOrderLine) {
                            matchedOrderLine.qtyOrdered = shipmentPackageLine.qtyOrdered;
                        }

                        if (!matchedOrderLine) {
                            matchedOrderLine = grouped[deliveryId].orderLine.find(
                                orderLine => orderLine.productErpNumber === shipmentPackageLine.productName,
                            );
                            if (matchedOrderLine) {
                                matchedOrderLine.qtyOrdered = shipmentPackageLine.qtyShipped;
                                matchedOrderLine.lineTotal = shipmentPackageLine.price;
                                matchedOrderLine.lineTotalDisplay = `$${shipmentPackageLine.price}`;
                                matchedOrderLine.extendedUnitNetPrice = shipmentPackageLine.price;
                                matchedOrderLine.extendedUnitNetPriceDisplay = `$${shipmentPackageLine.price}`;
                            }
                        }
                    });
                }
            });
            return grouped;
        }, {});
    };

    let groupedOrderLines;

    if (fields.groupByShipmentPackage) {
        groupedOrderLines = groupByShipmentPackageId(order?.orderLines, order?.shipmentPackages || []);
    } else {
        groupedOrderLines = groupByDeliveryId(order?.orderLines, order?.shipmentPackages || []);
    }

    Object.entries(groupedOrderLines).forEach(([groupedOrderLinesId, orderLines]) => {
        if (order.properties?.realtimeOrderStatus) {
            const realtimeOrderStatuses = JSON.parse(order.properties?.realtimeOrderStatus);

            realtimeOrderStatuses.forEach((realtimeOrderStatus: any) => {
                let matchedOrder;
                if (fields.groupByShipmentPackage) {
                    matchedOrder = realtimeOrderStatus.shipmentPackageId === orderLines.shipmentPackageId;
                } else {
                    matchedOrder = realtimeOrderStatus.deliveryId === orderLines.deliveryId;
                }
                if (matchedOrder) {
                    groupedOrderLines[groupedOrderLinesId] = {
                        ...groupedOrderLines[groupedOrderLinesId],
                        realtimeOrderStatus,
                    };
                }
            });
        } else if (order.shipmentPackages) {
            const shipmentPackages = order.shipmentPackages;
            shipmentPackages.forEach(shipmentPackage => {
                groupedOrderLines[groupedOrderLinesId] = {
                    ...groupedOrderLines[groupedOrderLinesId],
                    realtimeOrderStatus: {
                        trackingURL: shipmentPackage.trackingUrl,
                        shipVia: shipmentPackage.shipVia,
                        trackingNumber: shipmentPackage.trackingNumber,
                    },
                };
            });
        }
    });

    const getOrderStatus = (groupedOrderLines: GroupedOrderLines) => {
        return groupedOrderLines.realtimeOrderStatus?.status
            ? groupedOrderLines.realtimeOrderStatus?.status
            : groupedOrderLines.shipmentPackageId &&
                shipmentStatusByshipmentPackageId[groupedOrderLines.shipmentPackageId].shipmentStatus
              ? shipmentStatusByshipmentPackageId[groupedOrderLines.shipmentPackageId].shipmentStatus
              : order.statusDisplay;
    };

    return (
        <StyledWrapper className="invoicehistory-wrapper" {...styles.invoiceHistoryWrapper}>
            {Object.entries(groupedOrderLines).map(([groupedOrderLinesId, groupedOrderLinesData], index) => (
                <StyledWrapper key={index}>
                    <StyledWrapper {...styles.invoiceItemWrapper}>
                        <StyledWrapper {...styles.firstCell}>
                            {groupedOrderLinesData.carrier && (
                                <SmallHeadingAndTextOrderLineDelivery
                                    heading={`DELIVERY ${
                                        Object.entries(groupedOrderLines).length > 1
                                            ? `${index + 1}/${Object.entries(groupedOrderLines).length}`
                                            : ""
                                    }`}
                                    text={
                                        (groupedOrderLinesData?.carrier?.toLowerCase() === "sales person delivery"
                                            ? "Sales Rep"
                                            : groupedOrderLinesData?.carrier) || ""
                                    }
                                    extendedStyles={styles.carrierHeadingAndText}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.firstCell}>
                            {(groupedOrderLinesData.realtimeOrderStatus?.status ||
                                (groupedOrderLinesData.shipmentPackageId &&
                                    shipmentStatusByshipmentPackageId[groupedOrderLinesData.shipmentPackageId]
                                        ?.shipmentStatus) ||
                                order.status) && (
                                <StatusLableAndHeading
                                    heading={translate("Status")}
                                    text={getOrderStatus(groupedOrderLinesData)}
                                    extendedStyles={styles.statusHeadingText}
                                />
                            )}
                        </StyledWrapper>
                        <StyledWrapper {...styles.SecondCell}>
                            {groupedOrderLinesData.shipmentPackageId &&
                                shipmentStatusByshipmentPackageId[groupedOrderLinesData.shipmentPackageId]
                                    ?.invoiceNumber && (
                                    <SmallHeadingAndTextOrderLineDelivery
                                        heading="Invoice#"
                                        text={
                                            (groupedOrderLinesData.shipmentPackageId &&
                                                shipmentStatusByshipmentPackageId[
                                                    groupedOrderLinesData.shipmentPackageId
                                                ]?.invoiceNumber) ||
                                            ""
                                        }
                                        extendedStyles={styles.invoiceNumberHeadingText}
                                    />
                                )}
                        </StyledWrapper>
                    </StyledWrapper>
                </StyledWrapper>
            ))}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(OrderDetailsInvoiceTable),
    definition: {
        allowedContexts: ["OrderDetailsPage", "VmiOrderDetailsPage", "CustomOrderStatusPage"],
        group: "Order Details",
        fieldDefinitions: [
            {
                name: fields.groupByShipmentPackage,
                displayName: "Group OrderLines By ShipmentPackage",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                tooltip:"Activate for real-time order history"
            },
        ],
    },
};

export default widgetModule;
